/**
 * PDFDocumentContent Component
 * Main PDF document structure
 * Design: Technical/Engineering with focus on readability
 */

import React from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { styles } from '../styles';
import { PDFDocumentProps } from '../types';
import { TimeInformationSection } from './TimeInformationSection';
import { ZeitschritteSection } from './ZeitschritteSection';
import { CSVFilesSection } from './CSVFilesSection';

export const PDFDocumentContent: React.FC<PDFDocumentProps> = ({ sessionId, data }) => {
  const { t, i18n } = useTranslation('pdfReport');
  const currentDate = new Date().toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Image src="/logo-pdf.png" style={{ width: 150, height: 45, marginBottom: 6 }} />
        <Text style={styles.title}>{t('document.title')}</Text>
        <View style={styles.titleUnderline} />

        {/* Session Info */}
        <View style={styles.sessionInfoContainer}>
          <Text style={styles.sessionInfo}>{t('document.createdLabel')} {currentDate}</Text>
          <Text style={styles.sessionInfo}>{t('document.sessionLabel')} {sessionId}</Text>
        </View>

        {/* 1. Features (input files) */}
        {data.files && data.files.length > 0 && (
          <CSVFilesSection files={data.files} variant="input" />
        )}

        {/* 2 + 3. Time information features + Detailed time categories */}
        {data.timeInfo && <TimeInformationSection timeInfo={data.timeInfo} />}

        {/* 4. Output (output files) */}
        {data.files && data.files.length > 0 && (
          <CSVFilesSection files={data.files} variant="output" />
        )}

        {/* 5. Time steps */}
        {data.zeitschritte && <ZeitschritteSection zeitschritte={data.zeitschritte} />}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Forecast Engine</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              t('document.pageOf', { pageNumber, totalPages })
            }
          />
        </View>
      </Page>
    </Document>
  );
};
